import { Controller } from "@hotwired/stimulus"

// <select multiple> をタグ形式の複数選択に拡張する。
// 元の <select multiple> はフォーム送信の真実源として残し、画面外へ隠す。その上に
// 「選択済みタグ（×で解除）＋検索入力」と、絞り込み済みの候補リストを描画する。
//
// 統制語彙（職種・業界・スキル）のように候補が多く複数選ぶ項目で使う。候補は optgroup の
// 見出しごとにまとめて表示し、選択済みの候補はリストから隠す。
//
// 使い方（Ca::ListHelper#tag_select / #taxonomy_tag_field が生成する）:
//   <div data-controller="tag-select" data-tag-select-placeholder-value="職種を選択">
//     <select multiple name="occupation[]" class="...">
//       <optgroup label="営業職"><option value="corporate-sales">法人営業</option></optgroup>
//     </select>
//   </div>
export default class extends Controller {
  static values = { placeholder: String }

  connect() {
    this.select = this.element.querySelector("select")
    if (!this.select) return

    // 元の <select> は残したまま視覚的に隠す（searchable-select と同じ方針）。
    // display:none にすると一部ブラウザで送信・検証の対象から外れるため画面外へ逃がす。
    Object.assign(this.select.style, {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: "0"
    })
    this.select.setAttribute("tabindex", "-1")
    this.select.setAttribute("aria-hidden", "true")

    this.buildUi()
    this.render()

    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    if (this.onDocumentClick) document.removeEventListener("click", this.onDocumentClick)
  }

  // <select> の option を { value, label, group, selected } の配列として毎回読み直す。
  // DOM を真実源にしておくと、選択状態を別の場所（チップの解除など）から変えられても追随できる。
  options() {
    return Array.from(this.select.options)
      .filter((option) => option.value !== "")
      .map((option) => ({
        value: option.value,
        label: option.text,
        group: option.parentElement?.tagName === "OPTGROUP" ? option.parentElement.label : "",
        selected: option.selected
      }))
  }

  selectedOptions() {
    return this.options().filter((option) => option.selected)
  }

  buildUi() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "relative"

    // タグと検索入力を1つの枠に収め、通常の入力欄と同じ見た目にする。
    this.box = document.createElement("div")
    this.box.className =
      "flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 " +
      "text-sm shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500"
    this.box.addEventListener("mousedown", (event) => {
      // タグの×以外をクリックしたら入力へフォーカスしてリストを開く。
      if (event.target.closest("button")) return

      event.preventDefault()
      this.input.focus()
      this.open()
    })

    this.tags = document.createElement("div")
    this.tags.className = "contents"

    this.input = document.createElement("input")
    this.input.type = "text"
    this.input.autocomplete = "off"
    this.input.className = "min-w-24 flex-1 border-0 bg-transparent py-1 text-sm text-gray-700 outline-none " +
      "placeholder:text-gray-400"
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-expanded", "false")

    this.list = document.createElement("ul")
    this.list.className =
      "absolute z-20 mt-1 max-h-60 w-full list-none overflow-auto rounded-md border border-gray-200 bg-white " +
      "py-1 text-sm shadow-lg hidden"
    this.list.setAttribute("role", "listbox")

    this.box.appendChild(this.tags)
    this.box.appendChild(this.input)
    this.wrapper.appendChild(this.box)
    this.wrapper.appendChild(this.list)
    this.element.appendChild(this.wrapper)

    this.input.addEventListener("focus", () => this.open())
    this.input.addEventListener("input", () => this.renderList())
    this.input.addEventListener("keydown", (event) => this.onKeydown(event))
    this.element.addEventListener("focusout", (event) => {
      if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.close()
    })
  }

  // 選択済みタグとプレースホルダを描き直す。リストが開いていれば候補も更新する。
  render() {
    const selected = this.selectedOptions()
    this.tags.replaceChildren(...selected.map((option) => this.buildTag(option)))
    // 1つでも選ばれていれば入力欄は「追加」の口になるので、プレースホルダは出さない。
    this.input.placeholder = selected.length > 0 ? "" : (this.placeholderValue || "選択してください")
    if (!this.list.classList.contains("hidden")) this.renderList()
  }

  buildTag(option) {
    const tag = document.createElement("span")
    tag.className =
      "inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 ring-1 ring-indigo-100"
    tag.appendChild(document.createTextNode(option.label))

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "cursor-pointer leading-none text-indigo-400 hover:text-indigo-700"
    remove.setAttribute("aria-label", `${option.label} を解除`)
    remove.textContent = "×"
    remove.addEventListener("click", () => this.deselect(option.value))
    tag.appendChild(remove)

    return tag
  }

  open() {
    if (this.list.classList.contains("hidden")) {
      this.list.classList.remove("hidden")
      this.input.setAttribute("aria-expanded", "true")
    }
    this.renderList()
  }

  close() {
    this.list.classList.add("hidden")
    this.input.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
    this.input.value = ""
  }

  // 未選択かつ検索語に一致する候補を、大分類（optgroup）の見出し付きで描画する。
  renderList() {
    const needle = this.input.value.trim().toLowerCase()
    this.visibleOptions = this.options().filter((option) => {
      if (option.selected) return false

      return needle === "" || option.label.toLowerCase().includes(needle)
    })
    if (this.activeIndex === undefined || this.activeIndex >= this.visibleOptions.length) this.activeIndex = -1
    if (this.visibleOptions.length > 0 && this.activeIndex < 0) this.activeIndex = 0

    this.list.replaceChildren()
    if (this.visibleOptions.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-gray-400"
      empty.textContent = "該当なし"
      this.list.appendChild(empty)
      return
    }

    let currentGroup = null
    this.itemElements = []
    this.visibleOptions.forEach((option, index) => {
      if (option.group && option.group !== currentGroup) {
        currentGroup = option.group
        const heading = document.createElement("li")
        heading.className = "px-3 pb-0.5 pt-2 text-xs font-medium text-gray-400"
        heading.textContent = option.group
        this.list.appendChild(heading)
      }

      const item = document.createElement("li")
      item.className = this.itemClass(index === this.activeIndex)
      item.textContent = option.label
      item.setAttribute("role", "option")
      // mousedown で確定する（input の blur より先に処理してリストが閉じないようにする）。
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.selectValue(option.value)
      })
      item.addEventListener("mouseenter", () => {
        this.activeIndex = index
        this.highlight()
      })
      this.list.appendChild(item)
      this.itemElements.push(item)
    })
  }

  itemClass(active) {
    return `cursor-pointer px-3 py-2 ${active ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`
  }

  highlight() {
    if (!this.itemElements) return

    this.itemElements.forEach((item, index) => {
      item.className = this.itemClass(index === this.activeIndex)
    })
    const active = this.itemElements[this.activeIndex]
    if (active) active.scrollIntoView({ block: "nearest" })
  }

  selectValue(value) {
    this.setSelected(value, true)
    // 続けて選べるよう、検索語だけ消してリストは開いたままにする。
    this.input.value = ""
    this.input.focus()
  }

  deselect(value) {
    this.setSelected(value, false)
  }

  // <select> 側の選択状態を更新し、change を発火する（auto-submit などの依存挙動と互換）。
  setSelected(value, selected) {
    const option = Array.from(this.select.options).find((candidate) => candidate.value === value)
    if (!option) return

    option.selected = selected
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
    this.render()
  }

  onKeydown(event) {
    // IME変換中の Enter・矢印は変換操作なので、コントローラ側では扱わない。
    if (event.isComposing || event.keyCode === 229) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (!this.list.classList.contains("hidden") && this.activeIndex >= 0) {
          event.preventDefault()
          this.selectValue(this.visibleOptions[this.activeIndex].value)
        }
        break
      case "Backspace":
        // 検索語が空のときだけ、直前のタグを外す（打ち間違いの修正を邪魔しない）。
        if (this.input.value === "") {
          const selected = this.selectedOptions()
          if (selected.length > 0) this.deselect(selected[selected.length - 1].value)
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  move(step) {
    if (this.list.classList.contains("hidden")) {
      this.open()
      return
    }
    if (!this.visibleOptions || this.visibleOptions.length === 0) return

    const count = this.visibleOptions.length
    this.activeIndex = this.activeIndex === -1 ? (step > 0 ? 0 : count - 1) : (this.activeIndex + step + count) % count
    this.highlight()
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }
}
